import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faBolt,
  faChevronDown,
  faCircleCheck,
  faCodeCommit,
  faComment,
  faEye,
  faFlag,
  faPlus,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

// ==================== Types ====================

export interface WaterfallEvent {
  id: string;
  timestamp: string;
  issueKey: string;
  issueSummary: string;
  type: string;
  author: string;
  platform: 'jira' | 'linear' | 'git';
  detail?: string;
  issueType?: string;
  estimatedStart?: string;
  estimatedEnd?: string;
}

interface PositionedEvent extends WaterfallEvent {
  xPct: number;
  lane: number; // 0 = center, positive = offset down, negative = offset up
}

interface IssueRow {
  issueKey: string;
  issueSummary: string;
  events: PositionedEvent[];
  firstTs: number;
  lastTs: number;
  platforms: Set<string>;
  maxLanes: number; // how many vertical lanes needed for this row
  issueType?: string;
  estimatedStartTs?: number;
  estimatedEndTs?: number;
}

// ==================== Helpers ====================

const EVENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  status_change: mkIcon(faArrowRightArrowLeft),
  assignment: mkIcon(faUsers),
  created: mkIcon(faPlus),
  completed: mkIcon(faCircleCheck),
  resolved: mkIcon(faCircleCheck),
  field_change: mkIcon(faEye),
  sprint_change: mkIcon(faFlag),
  priority_change: mkIcon(faFlag),
  comment: mkIcon(faComment),
  commit: mkIcon(faCodeCommit),
};

const PLATFORM_COLORS: Record<string, { bg: string; border: string; dot: string; text: string }> = {
  jira: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
  linear: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', dot: 'bg-indigo-500', text: 'text-indigo-600 dark:text-indigo-400' },
  git: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', dot: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' },
};

function formatDateTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

// Minimum percent distance between dots before they are considered overlapping
const OVERLAP_THRESHOLD_PCT = 1.8;
const DOT_SIZE = 20; // px
const LANE_SPACING = 12; // px offset per lane

// Assign lanes to events in a row to avoid overlaps
function assignLanes(events: WaterfallEvent[], getXPct: (ts: string) => number): { positioned: PositionedEvent[]; maxLanes: number } {
  const sorted = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const positioned: PositionedEvent[] = [];
  // Track occupied x-ranges per lane
  const laneEnds: number[] = []; // laneEnds[i] = rightmost xPct used in lane i

  for (const evt of sorted) {
    const xPct = getXPct(evt.timestamp);
    // Find a lane where this event doesn't collide
    let assignedLane = 0;
    for (let lane = 0; lane < laneEnds.length; lane++) {
      if (xPct - laneEnds[lane] >= OVERLAP_THRESHOLD_PCT) {
        assignedLane = lane;
        break;
      }
      assignedLane = lane + 1;
    }
    if (assignedLane >= laneEnds.length) laneEnds.push(xPct);
    else laneEnds[assignedLane] = xPct;

    positioned.push({ ...evt, xPct, lane: assignedLane });
  }
  return { positioned, maxLanes: Math.max(laneEnds.length, 1) };
}


// ==================== Component ====================

interface WaterfallViewProps {
  events: WaterfallEvent[];
  title?: string;
  onEventClick?: (event: WaterfallEvent) => void;
}

export function WaterfallView({ events, title, onEventClick }: WaterfallViewProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<{ x: number; y: number } | null>(null);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [epicsOnly, setEpicsOnly] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if any events have issueType data (enables epic toggle)
  const hasEpics = useMemo(() => events.some(e => e.issueType?.toLowerCase() === 'epic'), [events]);

  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Time range
  const { minTs, timeSpanMs } = useMemo(() => {
    if (events.length === 0) return { minTs: 0, timeSpanMs: 1 };
    let min = Infinity, max = -Infinity;
    for (const e of events) {
      const ts = new Date(e.timestamp).getTime();
      if (ts < min) min = ts;
      if (ts > max) max = ts;
    }
    const span = max - min || 3600000;
    const pad = span * 0.05;
    return { minTs: min - pad, timeSpanMs: span + pad * 2 };
  }, [events]);

  const getXPosition = useCallback((timestamp: string): number => {
    const ts = new Date(timestamp).getTime();
    return ((ts - minTs) / timeSpanMs) * 100;
  }, [minTs, timeSpanMs]);

  // Filter events based on epic toggle
  const filteredEvents = useMemo(() => {
    if (!epicsOnly) return events;
    // When epicsOnly, show only events whose issueType is Epic
    return events.filter(e => e.issueType?.toLowerCase() === 'epic');
  }, [events, epicsOnly]);

  // Group events by issue and assign lanes
  const rows: IssueRow[] = useMemo(() => {
    const map = new Map<string, { issueKey: string; issueSummary: string; events: WaterfallEvent[]; firstTs: number; lastTs: number; platforms: Set<string>; issueType?: string; estimatedStartTs?: number; estimatedEndTs?: number }>();
    for (const e of filteredEvents) {
      if (!map.has(e.issueKey)) {
        map.set(e.issueKey, {
          issueKey: e.issueKey,
          issueSummary: e.issueSummary,
          events: [],
          firstTs: Infinity,
          lastTs: -Infinity,
          platforms: new Set(),
          issueType: e.issueType,
          estimatedStartTs: e.estimatedStart ? new Date(e.estimatedStart).getTime() : undefined,
          estimatedEndTs: e.estimatedEnd ? new Date(e.estimatedEnd).getTime() : undefined,
        });
      }
      const row = map.get(e.issueKey)!;
      row.events.push(e);
      const ts = new Date(e.timestamp).getTime();
      if (ts < row.firstTs) row.firstTs = ts;
      if (ts > row.lastTs) row.lastTs = ts;
      row.platforms.add(e.platform);
      // Merge estimated dates from any event that has them
      if (e.estimatedStart && !row.estimatedStartTs) row.estimatedStartTs = new Date(e.estimatedStart).getTime();
      if (e.estimatedEnd && !row.estimatedEndTs) row.estimatedEndTs = new Date(e.estimatedEnd).getTime();
    }

    return Array.from(map.values())
      .sort((a, b) => a.firstTs - b.firstTs)
      .map(row => {
        const { positioned, maxLanes } = assignLanes(row.events, getXPosition);
        return { ...row, events: positioned, maxLanes };
      });
  }, [filteredEvents, getXPosition]);

  // Time axis ticks
  const timeTicks = useMemo(() => {
    const ticks: { pos: number; label: string }[] = [];
    const targetTickCount = 8;
    const stepMs = timeSpanMs / targetTickCount;
    for (let i = 0; i <= targetTickCount; i++) {
      const ts = minTs + stepMs * i;
      const pos = ((ts - minTs) / timeSpanMs) * 100;
      const d = new Date(ts);
      const label = timeSpanMs > 172800000
        ? d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
        : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      ticks.push({ pos, label });
    }
    return ticks;
  }, [minTs, timeSpanMs]);

  const hoveredEventData = hoveredEvent ? events.find(e => e.id === hoveredEvent) : null;

  if (events.length === 0) return null;

  const BASE_ROW_HEIGHT = 36;
  const LABEL_WIDTH = 180;

  return (
    <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBolt} className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">{title || 'Waterfall Timeline'}</span>
          <span className="text-[10px] text-gray-400 dark:text-slate-500">
            ({rows.length} issues · {filteredEvents.length} events)
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasEpics && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setEpicsOnly(v => !v); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setEpicsOnly(v => !v); } }}
              className={`text-[10px] px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer select-none ${
                epicsOnly
                  ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-500/30'
                  : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 border border-transparent hover:bg-gray-200 dark:hover:bg-slate-700/50'
              }`}
            >
              Epics Only
            </span>
          )}
          <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 dark:text-slate-400 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </div>
      </button>

      {!collapsed && (
        <div className="relative" ref={containerRef}>
          {/* Tooltip — rendered via portal to avoid overflow clipping */}
          {hoveredEventData && tooltipAnchor && createPortal(
            <div
              className="fixed z-[9999] pointer-events-none px-3 py-2 rounded-lg shadow-xl text-xs max-w-[300px]"
              style={{
                left: Math.min(tooltipAnchor.x, window.innerWidth - 310),
                top: Math.max(tooltipAnchor.y - 90, 8),
                backgroundColor: isDark ? '#1e293b' : '#fff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
                color: isDark ? '#fff' : '#111',
              }}
            >
              <div className="font-semibold">{hoveredEventData.issueKey}</div>
              <div className="text-gray-500 dark:text-slate-400 truncate">{hoveredEventData.issueSummary}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${PLATFORM_COLORS[hoveredEventData.platform]?.bg || ''} ${PLATFORM_COLORS[hoveredEventData.platform]?.text || ''}`}>
                  {hoveredEventData.platform}
                </span>
                <span className="capitalize">{hoveredEventData.type.replace(/_/g, ' ')}</span>
              </div>
              <div className="mt-0.5 text-gray-400 dark:text-slate-500">
                {hoveredEventData.author} · {formatDateTime(hoveredEventData.timestamp)}
              </div>
              {hoveredEventData.detail && (
                <div className="mt-0.5 text-gray-500 dark:text-slate-400 truncate">{hoveredEventData.detail}</div>
              )}
              {onEventClick && (
                <div className="mt-1 text-[9px] text-indigo-400 dark:text-indigo-300">Click to open issue</div>
              )}
            </div>,
            document.body
          )}

          {/* Time axis header */}
          <div className="flex border-b border-gray-200 dark:border-slate-700/30">
            <div
              className="flex-shrink-0 px-3 py-1.5 text-[10px] font-medium text-gray-500 dark:text-slate-500 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700/30"
              style={{ width: LABEL_WIDTH }}
            >
              Issue
            </div>
            <div className="flex-1 relative h-6 overflow-hidden">
              {timeTicks.map((tick, i) => (
                <div
                  key={i}
                  className="absolute top-0 h-full flex items-center"
                  style={{ left: `${tick.pos}%` }}
                >
                  <span className="text-[9px] text-gray-400 dark:text-slate-500 whitespace-nowrap -translate-x-1/2">
                    {tick.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
            {rows.map((row) => {
              const barLeft = ((row.firstTs - minTs) / timeSpanMs) * 100;
              const barWidth = Math.max(((row.lastTs - row.firstTs) / timeSpanMs) * 100, 0.5);
              const rowHeight = BASE_ROW_HEIGHT + Math.max(0, row.maxLanes - 1) * LANE_SPACING;

              return (
                <div
                  key={row.issueKey}
                  className="flex border-b border-gray-100 dark:border-slate-700/15 hover:bg-gray-50/50 dark:hover:bg-slate-700/10 transition-colors"
                  style={{ minHeight: rowHeight }}
                >
                  {/* Label */}
                  <div
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 border-r border-gray-200 dark:border-slate-700/30 min-w-0"
                    style={{ width: LABEL_WIDTH }}
                  >
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[...row.platforms].map(p => (
                        <div key={p} className={`h-1.5 w-1.5 rounded-full ${PLATFORM_COLORS[p]?.dot || 'bg-gray-400'}`} title={p} />
                      ))}
                    </div>
                    <span className="text-[10px] font-mono text-blue-600 dark:text-blue-400 flex-shrink-0">
                      {row.issueKey}
                    </span>
                    <span className="text-[10px] text-gray-600 dark:text-slate-400 truncate">
                      {row.issueSummary}
                    </span>
                  </div>

                  {/* Timeline area */}
                  <div className="flex-1 relative overflow-hidden">
                    {/* Vertical grid lines */}
                    {timeTicks.map((tick, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 border-l border-gray-100 dark:border-slate-700/10"
                        style={{ left: `${tick.pos}%` }}
                      />
                    ))}

                    {/* Estimated timeline bar (dashed) — shown when row has estimated dates */}
                    {row.estimatedStartTs && row.estimatedEndTs && (() => {
                      const estLeft = ((row.estimatedStartTs! - minTs) / timeSpanMs) * 100;
                      const estWidth = Math.max(((row.estimatedEndTs! - row.estimatedStartTs!) / timeSpanMs) * 100, 0.5);
                      return (
                        <div
                          className="absolute rounded-full"
                          title="Estimated timeline"
                          style={{
                            left: `${estLeft}%`,
                            width: `${estWidth}%`,
                            height: 4,
                            top: 'calc(50% - 6px)',
                            backgroundColor: row.platforms.has('jira') ? '#3b82f6' : '#6366f1',
                            opacity: 0.2,
                            minWidth: 4,
                            border: '1px dashed',
                            borderColor: row.platforms.has('jira') ? '#3b82f6' : '#6366f1',
                          }}
                        />
                      );
                    })()}

                    {/* Actual bar spanning first to last event */}
                    <div
                      className="absolute rounded-full"
                      title={row.estimatedStartTs ? 'Actual timeline' : undefined}
                      style={{
                        left: `${barLeft}%`,
                        width: `${barWidth}%`,
                        height: 4,
                        top: row.estimatedStartTs && row.estimatedEndTs ? 'calc(50% + 2px)' : 'calc(50% - 2px)',
                        backgroundColor: row.platforms.has('git') ? '#f97316' : row.platforms.has('jira') ? '#3b82f6' : '#6366f1',
                        opacity: 0.5,
                        minWidth: 4,
                      }}
                    />

                    {/* Event dots — positioned with lane offsets to avoid overlap */}
                    {row.events.map((evt) => {
                      const pColor = PLATFORM_COLORS[evt.platform] || PLATFORM_COLORS.jira;
                      const Icon = EVENT_ICONS[evt.type] || mkIcon(faBolt);
                      const isHovered = hoveredEvent === evt.id;
                      // Center the base at 50%, then offset by lane
                      const centerPx = rowHeight / 2;
                      const laneOffset = (evt.lane - (row.maxLanes - 1) / 2) * LANE_SPACING;
                      const topPx = centerPx + laneOffset - DOT_SIZE / 2;

                      return (
                        <div
                          key={evt.id}
                          className={`absolute -translate-x-1/2 cursor-pointer transition-transform ${isHovered ? 'scale-[1.4] z-10' : 'hover:scale-110'}`}
                          style={{ left: `${evt.xPct}%`, top: topPx }}
                          onMouseEnter={(e) => {
                            setHoveredEvent(evt.id);
                            setTooltipAnchor({ x: e.clientX + 12, y: e.clientY });
                          }}
                          onMouseMove={(e) => {
                            setTooltipAnchor({ x: e.clientX + 12, y: e.clientY });
                          }}
                          onMouseLeave={() => {
                            setHoveredEvent(null);
                            setTooltipAnchor(null);
                          }}
                          onClick={() => onEventClick?.(evt)}
                        >
                          <div
                            className={`flex items-center justify-center rounded-full ${pColor.bg} border ${pColor.border}`}
                            style={{ width: DOT_SIZE, height: DOT_SIZE }}
                          >
                            <Icon className={`h-2.5 w-2.5 ${pColor.text}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="px-5 py-2.5 border-t border-gray-200 dark:border-slate-700/30 flex items-center gap-4 flex-wrap">
            {Object.entries(PLATFORM_COLORS).map(([platform, colors]) => {
              const has = filteredEvents.some(e => e.platform === platform);
              if (!has) return null;
              return (
                <div key={platform} className="flex items-center gap-1.5">
                  <div className={`h-2.5 w-2.5 rounded-full ${colors.dot}`} />
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 capitalize">{platform}</span>
                </div>
              );
            })}
            <div className="border-l border-gray-200 dark:border-slate-700/30 h-4 mx-1" />
            {Object.entries(EVENT_ICONS).map(([type, Icon]) => {
              const has = filteredEvents.some(e => e.type === type);
              if (!has) return null;
              return (
                <div key={type} className="flex items-center gap-1">
                  <Icon className="h-3 w-3 text-gray-400 dark:text-slate-500" />
                  <span className="text-[10px] text-gray-400 dark:text-slate-500 capitalize">{type.replace(/_/g, ' ')}</span>
                </div>
              );
            })}
            {/* Estimated vs Actual bar legend */}
            {rows.some(r => r.estimatedStartTs && r.estimatedEndTs) && (
              <>
                <div className="border-l border-gray-200 dark:border-slate-700/30 h-4 mx-1" />
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-1 rounded-full bg-blue-500/25 border border-dashed border-blue-500/50" />
                  <span className="text-[10px] text-gray-400 dark:text-slate-500">Estimated</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-1 rounded-full bg-blue-500/50" />
                  <span className="text-[10px] text-gray-400 dark:text-slate-500">Actual</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WaterfallView;
